import { Component, ErrorInfo, ReactNode } from 'react';
import { Routes, Route } from 'react-router-dom';
import HeaderNavigation from "@/components/sections/header-navigation";
import Footer from "@/components/sections/footer";
import WhatsAppButton from "@/components/sections/whatsapp-button";
import HomePage from "@/pages/HomePage";
import EcommerceDeliveryPage from "@/pages/EcommerceDeliveryPage";
import HospitalityRetailPage from "@/pages/HospitalityRetailPage";
import ConstructionPage from "@/pages/ConstructionPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsConditionsPage from "@/pages/TermsConditionsPage";
import ContactPage from "@/pages/ContactPage";
import CompanyProfilePage from "@/pages/CompanyProfilePage";
import ComingSoonPage from "@/pages/ComingSoonPage";

interface ErrorBoundaryProps {
  children: ReactNode;
  componentName: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`Error in ${this.props.componentName}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', border: '2px solid red', margin: '10px', backgroundColor: '#fff3f3' }}>
          <h3 style={{ color: 'red' }}>Error in {this.props.componentName}</h3>
          <p>{this.state.error?.message}</p>
          <details>
            <summary>Stack trace</summary>
            <pre style={{ fontSize: '12px', overflow: 'auto' }}>{this.state.error?.stack}</pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  return (
    <div className="overflow-x-hidden w-full">
      <ErrorBoundary componentName="HeaderNavigation">
        <HeaderNavigation />
      </ErrorBoundary>
      
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/ecommerce-delivery" element={<EcommerceDeliveryPage />} />
        <Route path="/hospitality" element={<HospitalityRetailPage />} />
        <Route path="/construction" element={<ConstructionPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-conditions" element={<TermsConditionsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        
        {/* About Us Pages */}
        <Route path="/company-profile" element={<CompanyProfilePage />} />
        <Route path="/mission-vision" element={<ComingSoonPage title="Vision & Mission" description="Discover our vision for the future and our mission to transform the manpower industry." />} />
        <Route path="/chairman-ceo" element={<ComingSoonPage title="Chairman & CEO Message" description="Read inspiring messages from our leadership team." />} />
        <Route path="/board-of-directors" element={<ComingSoonPage title="Board of Directors" description="Meet the experienced professionals guiding our organization." />} />
        <Route path="/accreditation" element={<ComingSoonPage title="Accreditation" description="View our certifications and industry accreditations." />} />
        <Route path="/recognition" element={<ComingSoonPage title="Recognition" description="See our awards and recognition in the industry." />} />
        
        {/* Our Expertise Pages - Coming Soon */}
        <Route path="/manpower-supply" element={<ComingSoonPage title="Manpower Solutions" description="Comprehensive workforce solutions tailored to your business needs." />} />
        <Route path="/security-services" element={<ComingSoonPage title="Security Services" description="Professional security services for your organization." />} />
        <Route path="/recruitment" element={<ComingSoonPage title="Executive Search & Recruitment" description="Find the best talent for your executive positions." />} />
        <Route path="/hr-consultancy" element={<ComingSoonPage title="HR Consultancy & Learning" description="Strategic HR solutions and training programs." />} />
        <Route path="/business-process-solutions" element={<ComingSoonPage title="Business Process Solutions" description="Streamline your operations with our BPS offerings." />} />
        
        {/* Sectors Pages - Coming Soon */}
        <Route path="/aviation-and-government" element={<ComingSoonPage title="Aviation" description="Specialized manpower solutions for the aviation sector." />} />
        <Route path="/banking-finance" element={<ComingSoonPage title="Banking & Finance" description="Expert staffing solutions for financial institutions." />} />
        <Route path="/entertainment-events" element={<ComingSoonPage title="Events & Entertainment" description="Professional event staffing and entertainment industry solutions." />} />
        <Route path="/health-care" element={<ComingSoonPage title="Healthcare" description="Quality healthcare staffing solutions for medical facilities." />} />
        <Route path="/it" element={<ComingSoonPage title="Technology Solutions" description="IT professionals and technical staffing services." />} />
        <Route path="/logistics" element={<ComingSoonPage title="Warehouse & Logistics" description="Efficient logistics and warehouse staffing solutions." />} />
        
        {/* News Pages - Coming Soon */}
        <Route path="/press-release" element={<ComingSoonPage title="Press Release" description="Latest news and press releases from IMMACULATE." />} />
        <Route path="/blog" element={<ComingSoonPage title="Blogs" description="Insights, trends, and updates from the manpower industry." />} />
        
        {/* Careers Page - Coming Soon */}
        <Route path="/jobs" element={<ComingSoonPage title="Careers" description="Explore exciting career opportunities with IMMACULATE." />} />
      </Routes>
      
      <ErrorBoundary componentName="Footer">
        <Footer />
      </ErrorBoundary>
      
      <WhatsAppButton />
    </div>
  );
}

export default App;

